<template>
  <div class="organization-positions-tab-wrapper">
    <div class="org-pos-tab">
      <OrganizationPositionsTable
        :positions="filteredPositions"
        :pagination="paginationData"
        :loading="isLoading"
        :searchText="searchText || viewState.state.filters.searchText"
        :appliedFilters="appliedFilters"
        :backendPagination="true"
        :title="title"
        :actionLabel="actionLabel"
        :hideTopSection="hideTopSection"
        :hideActiveNo="hideActiveNo"
        :activeRecordsCount="activeRecordsCount"
        @positionClick="handlePositionDrillDown"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed, getCurrentInstance, ref } from 'vue';
import OrganizationPositionsTable from '@components/organizations/OrganizationPositionsTable/OrganizationPositionsTable.vue';
import { usePositions } from "@/composables/positions/usePositions";
import { useHighlightRow } from "@/composables/common/useHighlightRow";
import { useOrganizationPositionsViewState } from '@components/organizations/OrganizationPositionsTable/useViewState';

const app = getCurrentInstance();
const { proxy } = app;

const emit = defineEmits(['drilldown']);

const positions = usePositions();
const { highlight } = useHighlightRow();
const viewState = useOrganizationPositionsViewState();

const props = defineProps({
  appliedFilters: {
    type: Array,
    default: () => [],
  },
  searchText: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: 'Positions Organization',
  },
  actionLabel: {
    type: String,
    default: 'Add Position',
  },
  hideTopSection: {
    type: Boolean,
    default: true,
  },
  hideActiveNo: {
    type: Boolean,
    default: false,
  },
  activeRecordsCount: {
    type: Number,
    default: null,
  },
});

const filteredPositions = computed(() => {
  return positions.allPositions.value || [];
});

const paginationData = computed(() => ({
  page: Math.floor(positions.paginationDefaultParams.value.offset / 10) + 1,
  total: positions.getPositionsCount(),
  perPage: 10,
}));

const isLoading = computed(() => proxy.$loader.isAppLoading);

const handlePositionDrillDown = (positionData) => {
  const posId = positionData.id || positionData.position?.data?.id;
  if (proxy?.$router && posId) {
    proxy.$router.push({
      name: 'PositionDetailChild',
      params: { positionId: posId },
      query: { positionTab: 'purpose' }
    }).catch(() => {});
  }
};

onMounted(async () => {
  proxy.$loader.show();
  
  const route = proxy?.$route;
  const router = proxy?.$router;
  const fromDashboard = route?.query?.fromDashboard === 'true';
  const searchPositionName = route?.query?.searchPosition || '';
  
  if (positions.getDynamicPositionFilters()?.name) {
    positions.removePositionFilter("name");
  }
  positions.setPaginationDataOffset(0);
  positions.clearPositionDynamicFilters();
  positions.addPositionFilter("archived", false);
  
  if (fromDashboard && searchPositionName && searchPositionName.trim() !== '') {
    await proxy.$nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const decodedName = decodeURIComponent(searchPositionName.trim());
    
    viewState.state.filters.searchText = decodedName;
    
    await proxy.$nextTick();
    
    positions.removePositionFilter("name");
    if (decodedName && decodedName.trim() !== "") {
      positions.addPositionFilter("name", decodedName);
    }
    positions.setPaginationDataOffset(0);
    await positions.loadAllPositions();
    
    await proxy.$nextTick();
    
    const allPositions = positions.allPositions.value || [];
    const foundPosition = allPositions.find(
      (pos) => pos.name === decodedName
    );
    
    if (foundPosition) {
      setTimeout(() => {
        highlight({ id: foundPosition.id, type: "Positions" });
      }, 500);
    }
    
    if (router && route) {
      router.replace({
        path: route.path,
        query: {}
      });
    }
  } else {
    await positions.loadAllPositions();
  }
  
  proxy.$loader.hide();
});
</script>
<style>
.org-pos-tab {
  box-shadow: 0px 1px 3px 0px #1018281A, 0px 1px 2px 0px #1018280F;
  border-radius: 8px;
  margin-top: 15px;
}
.organization-positions-tab-wrapper {
  width: 100%;
}
</style>